import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Checkbox,
  Code,
  Container,
  FileButton,
  Group,
  Loader,
  Modal,
  Pagination,
  Progress,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconArrowDown,
  IconArrowLeft,
  IconArrowsSort,
  IconArrowUp,
  IconBolt,
  IconCheck,
  IconCopy,
  IconDownload,
  IconRefresh,
  IconSearch,
  IconTrash,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../api/client";
import { localAgentFlash } from "../api/localAgent";
import type {
  Batch,
  DeviceLookup,
  FirmwareFile,
  FlashEvent,
  FlashHistoryEvent,
  FlashQueueDevice,
} from "../api/types";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useLocalAgent } from "../hooks/useLocalAgent";
import { ResponsiveTable } from "../components/responsiveTable/ResponsiveTable";

interface FlashState {
  device: FlashQueueDevice;
  firmware: string;
  stage: string;
  percent: number;
  log: string[];
  finished: boolean;
  success: boolean | null;
  error: string | null;
}

export function BatchFlash() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const batchId = Number(id);
  const user = useCurrentUser();
  const localAgent = useLocalAgent();
  const canEdit = user.role === "admin" || user.role === "operator";

  const [batch, setBatch] = useState<Batch | null>(null);
  const [firmware, setFirmware] = useState<FirmwareFile[]>([]);

  // Import-from-another-batch modal (global search across all batches)
  const [importOpen, setImportOpen] = useState(false);
  const [importBatches, setImportBatches] = useState<
    { batch_id: number; batch_code: string; files: FirmwareFile[] }[]
  >([]);
  // Selection identifies a file by "<batch_id>/<filename>" to support
  // picking files from multiple source batches in one go.
  const [importSelected, setImportSelected] = useState<Set<string>>(new Set());
  const [importLoading, setImportLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importSearch, setImportSearch] = useState("");
  const [importOnlyMatching, setImportOnlyMatching] = useState(true);
  const [queue, setQueue] = useState<FlashQueueDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [flash, setFlash] = useState<FlashState | null>(null);
  const flashingRef = useRef(false);
  const [previewDevice, setPreviewDevice] = useState<DeviceLookup | null>(null);
  const [previewState, setPreviewState] = useState<
    "idle" | "loading" | "ok" | "missing_fw" | "not_found" | "error"
  >("idle");
  const [history, setHistory] = useState<FlashHistoryEvent[]>([]);
  // The serial field is UNCONTROLLED (read/written via the ref) so typing does
  // NOT re-render this heavy page (batches can hold thousands of firmware rows)
  // — that per-keystroke re-render was freezing the input. We only read the
  // value on Enter (search) / flash.
  const serialInputRef = useRef<HTMLInputElement>(null);
  const getSerial = () => serialInputRef.current?.value.trim() ?? "";
  const setSerial = (v: string) => {
    if (serialInputRef.current) serialInputRef.current.value = v;
  };

  async function load() {
    setLoading(true);
    try {
      const [b, fw, q] = await Promise.all([
        api.getBatch(batchId),
        api.listBatchFirmware(batchId),
        api.listFlashQueue(batchId),
      ]);
      setBatch(b);
      setFirmware(fw);
      setQueue(q);
    } catch {
      notifications.show({ color: "red", title: t("common.error"), message: t("batchFlash.loadFailed") });
    } finally {
      setLoading(false);
    }
  }

  // After a flash only the queue changes — don't refetch thousands of firmware
  // rows or flip the page into the loading state.
  async function refreshQueue() {
    try {
      setQueue(await api.listFlashQueue(batchId));
    } catch {
      /* non-fatal */
    }
  }

  useEffect(() => {
    if (Number.isFinite(batchId)) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  // Load flash history on mount; pre-fill input from last successful flash
  useEffect(() => {
    if (!Number.isFinite(batchId)) return;
    api
      .listFlashHistory(batchId, 50)
      .then((events) => {
        setHistory(events);
        const lastOk = events.find((e) => e.success && e.serial_number);
        if (lastOk?.serial_number) {
          const n = Number(lastOk.serial_number);
          if (Number.isFinite(n)) setSerial(String(n + 1));
        }
      })
      .catch(() => {
        /* non-fatal */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  // Search the firmware preview on demand (Enter), not on every keystroke.
  async function searchPreview() {
    const value = getSerial();
    if (!value) {
      setPreviewDevice(null);
      setPreviewState("idle");
      return;
    }
    setPreviewState("loading");
    try {
      const d = await api.lookupDeviceBySerial(batchId, value);
      setPreviewDevice(d);
      setPreviewState(d.firmware_filename ? "ok" : "missing_fw");
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setPreviewDevice(null);
        setPreviewState("not_found");
      } else {
        setPreviewDevice(null);
        setPreviewState("error");
      }
    }
  }

  // Post-flash: prepend event to history; on success, auto-increment input
  useEffect(() => {
    if (!flash?.finished) return;
    const current = getSerial();
    const justFlashed = flash.device.serial_number ?? "";
    const matchesInput =
      current === justFlashed ||
      current.replace(/^0+/, "") === justFlashed.replace(/^0+/, "");

    setHistory((prev) =>
      [
        {
          id: Date.now(),
          created_at: new Date().toISOString(),
          device_id: flash.device.id,
          serial_number: flash.device.serial_number ?? null,
          dev_eui: flash.device.dev_eui,
          success: flash.success === true,
          firmware_filename: flash.firmware,
          username: null,
        },
        ...prev,
      ].slice(0, 200),
    );

    let autoSearched = false;
    if (flash.success && matchesInput) {
      const n = Number(current);
      if (Number.isFinite(n)) {
        setSerial(String(n + 1));
        // Auto-search the firmware for the next serial so the operator doesn't
        // have to press Search after every successful flash.
        autoSearched = true;
        void searchPreview();
      }
    }
    if (!autoSearched) {
      // Otherwise reset the preview so the next serial starts clean.
      setPreviewDevice(null);
      setPreviewState("idle");
    }

    requestAnimationFrame(() => {
      serialInputRef.current?.focus();
      serialInputRef.current?.select();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flash?.finished, flash?.success]);

  const nextDevice = useMemo(
    () => queue.find((d) => d.firmware_filename) ?? null,
    [queue],
  );

  const pendingWithoutFirmware = queue.filter((d) => !d.firmware_filename).length;

  const [onlyWithoutFw, setOnlyWithoutFw] = useState(false);
  const [exportingFw, setExportingFw] = useState(false);

  const displayedQueue = useMemo(
    () => (onlyWithoutFw ? queue.filter((d) => !d.firmware_filename) : queue),
    [queue, onlyWithoutFw],
  );

  async function exportWithoutFirmware() {
    const ids = queue.filter((d) => !d.firmware_filename).map((d) => d.id);
    if (ids.length === 0) return;
    setExportingFw(true);
    try {
      const { blob, filename } = await api.exportDevices(ids, "xlsx");
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      notifications.show({ color: "green", message: t("batchFlash.exportedWithoutFw", { count: ids.length }) });
    } catch (err) {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? JSON.stringify(err.detail) : String(err),
      });
    } finally {
      setExportingFw(false);
    }
  }

  async function handleUpload(files: File[] | null) {
    if (!files || files.length === 0) return;
    // Upload in chunks so large batches (thousands of .elf) show progress and
    // don't hit a single huge multipart POST. Each chunk is committed server-side.
    const CHUNK = 100;
    const all = Array.from(files);
    setUploading(true);
    setUploadProgress({ done: 0, total: all.length });
    let saved = 0;
    const errors: { filename: string; error: string }[] = [];
    try {
      for (let i = 0; i < all.length; i += CHUNK) {
        const chunk = all.slice(i, i + CHUNK);
        const r = await api.uploadBatchFirmware(batchId, chunk);
        saved += r.saved.length;
        errors.push(...r.errors);
        setUploadProgress({ done: Math.min(i + CHUNK, all.length), total: all.length });
      }
      notifications.show({
        color: errors.length > 0 ? "yellow" : "green",
        message: t("batchFlash.filesSaved", {
          total: saved,
          rejected: errors.length > 0 ? t("batchFlash.filesRejected", { count: errors.length }) : "",
        }),
      });
      if (errors.length > 0) {
        notifications.show({
          color: "red",
          title: t("batchFlash.filesRejectedTitle"),
          message: errors.slice(0, 3).map((e) => `${e.filename}: ${e.error}`).join(" | "),
        });
      }
      await load();
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      notifications.show({
        color: "red",
        title: t("batchFlash.uploadFailed"),
        message: t("batchFlash.uploadPartial", { saved }) + " " + JSON.stringify(detail).slice(0, 160),
      });
      await load();
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  }

  async function handleDelete(filename: string) {
    if (!window.confirm(t("batchFlash.confirmDeleteFile", { filename }))) return;
    try {
      await api.deleteBatchFirmware(batchId, filename);
      await load();
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      notifications.show({
        color: "red",
        title: t("batchFlash.deleteFailed"),
        message: JSON.stringify(detail).slice(0, 200),
      });
    }
  }

  // Stable identity so the memoized FirmwareTable doesn't re-render on every
  // flash progress event / preview change.
  const handleDeleteRef = useRef(handleDelete);
  handleDeleteRef.current = handleDelete;
  const onDeleteFw = useCallback((filename: string) => void handleDeleteRef.current(filename), []);

  async function handleDeleteAll() {
    if (!window.confirm(t("batchFlash.confirmDeleteAll", { count: firmware.length }))) return;
    try {
      const removed = await api.deleteAllBatchFirmware(batchId);
      await load();
      notifications.show({
        color: "teal",
        message: t("batchFlash.deleteAllOk", { count: removed }),
      });
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      notifications.show({
        color: "red",
        title: t("batchFlash.deleteFailed"),
        message: JSON.stringify(detail).slice(0, 200),
      });
    }
  }

  async function openImportModal() {
    setImportOpen(true);
    setImportSelected(new Set());
    setImportSearch("");
    setImportOnlyMatching(true);
    setImportLoading(true);
    try {
      const list = await api.listFirmwareAllBatches(batchId);
      setImportBatches(list);
    } catch (err) {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : String(err),
      });
    } finally {
      setImportLoading(false);
    }
  }

  // Set of serials in the current batch (used to flag matching firmwares in
  // the import modal). Includes pending + already-flashed devices via queue.
  const batchSerials = useMemo(() => {
    const s = new Set<string>();
    for (const d of queue) {
      if (d.serial_number) {
        s.add(d.serial_number);
        s.add(d.serial_number.replace(/^0+/, "") || "0");
      }
    }
    return s;
  }, [queue]);

  function fileMatches(f: FirmwareFile): boolean {
    return batchSerials.has(f.supplier_id) ||
      batchSerials.has(f.supplier_id.replace(/^0+/, "") || "0");
  }

  // Selection key = "<batch_id>/<filename>" — encodes the source.
  function selKey(batchId: number, filename: string): string {
    return `${batchId}/${filename}`;
  }

  function toggleImportFile(batchId: number, filename: string) {
    setImportSelected((prev) => {
      const k = selKey(batchId, filename);
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }

  function toggleImportAll(items: { batch_id: number; file: FirmwareFile }[]) {
    setImportSelected((prev) => {
      const keys = items.map((it) => selKey(it.batch_id, it.file.filename));
      const allSelected = keys.length > 0 && keys.every((k) => prev.has(k));
      const next = new Set(prev);
      if (allSelected) {
        for (const k of keys) next.delete(k);
      } else {
        for (const k of keys) next.add(k);
      }
      return next;
    });
  }

  async function runImport() {
    if (importSelected.size === 0) return;
    setImporting(true);
    // Group selected files by source batch_id so we can call the endpoint once per source
    const groups = new Map<number, string[]>();
    for (const k of importSelected) {
      const slash = k.indexOf("/");
      const bid = Number(k.slice(0, slash));
      const fn = k.slice(slash + 1);
      if (!groups.has(bid)) groups.set(bid, []);
      groups.get(bid)!.push(fn);
    }
    let totalCopied = 0;
    let totalErrors = 0;
    try {
      for (const [bid, filenames] of groups) {
        try {
          const res = await api.importFirmwareFromBatch(batchId, bid, filenames);
          totalCopied += res.copied.length;
          totalErrors += res.errors.length;
        } catch (err) {
          totalErrors += filenames.length;
          notifications.show({
            color: "red",
            message: err instanceof ApiError ? err.message : String(err),
          });
        }
      }
      await load();
      notifications.show({
        color: totalErrors > 0 ? "yellow" : "teal",
        message: t("batchFlash.importDone", { copied: totalCopied, errors: totalErrors }),
      });
      if (totalErrors === 0) setImportOpen(false);
    } finally {
      setImporting(false);
    }
  }

  async function startFlash(device: FlashQueueDevice) {
    if (flashingRef.current) return;
    if (!device.firmware_filename) return;
    flashingRef.current = true;
    setFlash({
      device,
      firmware: device.firmware_filename,
      stage: "starting",
      percent: 0,
      log: [],
      finished: false,
      success: null,
      error: null,
    });

    try {
      if (localAgent.stm32) {
        let lastSuccess = false;
        let lastReturncode: number | null = null;
        await localAgentFlash(device.firmware_batch_id ?? batchId, device.firmware_filename!, (event: FlashEvent) => {
          if (event.type === "done") {
            lastSuccess = event.success;
            lastReturncode = event.returncode;
          }
          if (event.type === "finished") lastSuccess = event.success;
          setFlash((prev) => {
            if (!prev) return prev;
            switch (event.type) {
              case "stage":
                return { ...prev, stage: event.stage };
              case "progress":
                return { ...prev, stage: event.stage, percent: event.percent };
              case "log":
                return { ...prev, log: [...prev.log.slice(-40), event.line] };
              case "done":
                return {
                  ...prev,
                  stage: event.success ? "done" : "failed",
                  percent: event.success ? 100 : prev.percent,
                  log: [...prev.log, ...event.lines.slice(-5)].slice(-50),
                  success: event.success,
                };
              case "error":
                return { ...prev, error: event.message, success: false };
              case "finished":
                return { ...prev, finished: true, success: event.success };
              default:
                return prev;
            }
          });
        }, localAgent.selectedId);
        await api.reportFlashResult(device.id, lastSuccess, lastReturncode, device.firmware_filename);
      } else {
        await api.flashDevice(device.id, (event: FlashEvent) => {
          setFlash((prev) => {
            if (!prev) return prev;
            switch (event.type) {
              case "started":
                return { ...prev, stage: "connecting" };
              case "stage":
                return { ...prev, stage: event.stage };
              case "progress":
                return { ...prev, stage: event.stage, percent: event.percent };
              case "log":
                return { ...prev, log: [...prev.log.slice(-40), event.line] };
              case "done":
                return {
                  ...prev,
                  stage: event.success ? "done" : "failed",
                  percent: event.success ? 100 : prev.percent,
                  log: [...prev.log, ...event.lines.slice(-5)].slice(-50),
                  success: event.success,
                };
              case "error":
                return { ...prev, error: event.message, success: false };
              case "finished":
                return { ...prev, finished: true, success: event.success };
              default:
                return prev;
            }
          });
        });
      }
    } catch (err) {
      const detail = err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
      setFlash((prev) =>
        prev ? { ...prev, error: detail.slice(0, 300), finished: true, success: false } : prev,
      );
    } finally {
      flashingRef.current = false;
      await refreshQueue();
    }
  }

  async function handleQuickFlash() {
    const value = getSerial();
    if (!value) return;
    if (flashingRef.current) return;

    let device: DeviceLookup | null = previewDevice;
    if (previewState === "loading") {
      const start = Date.now();
      while (previewState === "loading" && Date.now() - start < 1000) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 50));
      }
      device = previewDevice;
    }

    if (!device) {
      try {
        device = await api.lookupDeviceBySerial(batchId, value);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          notifications.show({
            color: "red",
            message: t("batchFlash.quick.notFound", { serial: value }),
          });
        } else {
          notifications.show({
            color: "red",
            message: t("batchFlash.quick.lookupFailed"),
          });
        }
        return;
      }
    }

    if (!device.firmware_filename) {
      notifications.show({
        color: "yellow",
        message: t("batchFlash.quick.missingFw", { serial: value }),
      });
      return;
    }

    const flashTarget = {
      ...device,
      batch_id: batchId,
      join_eui: null,
      app_key: null,
      model: null,
      firmware_version: null,
      qr_payload: null,
      shipment_id: null,
      scanned_at: null,
    } as FlashQueueDevice;
    await startFlash(flashTarget);
  }

  if (!Number.isFinite(batchId)) return <Text c="red">{t("batchFlash.invalidId")}</Text>;

  return (
    <Container size="xl">
      <Group justify="space-between" mb="sm">
        <Anchor component={Link} to={`/batches/${batchId}`} size="sm">
          <Group gap={4}>
            <IconArrowLeft size={14} />
            {t("batchFlash.back")}
          </Group>
        </Anchor>
        <Button
          variant="subtle"
          leftSection={<IconRefresh size={14} />}
          onClick={load}
          loading={loading}
        >
          {t("batchFlash.reload")}
        </Button>
      </Group>

      <Title order={2} mb="xs">
        {t("batchFlash.headerTitle", { code: batch?.code ?? "..." })}
      </Title>
      {localAgent.available && (
        <Badge
          variant="dot"
          color={localAgent.stm32 ? "green" : "yellow"}
          mb="sm"
        >
          {localAgent.stm32
            ? t("batchFlash.localAgentReady")
            : t("batchFlash.localAgentNoStm32")}
        </Badge>
      )}
      <Group mb="lg" gap="xs">
        <Badge variant="light">{t("batchFlash.pendingBadge", { count: queue.length })}</Badge>
        <Badge variant="light" color="blue">
          {t("batchFlash.fwFilesBadge", { count: firmware.length })}
        </Badge>
        {pendingWithoutFirmware > 0 && (
          <>
            <Badge
              color="yellow"
              variant={onlyWithoutFw ? "filled" : "light"}
              style={{ cursor: "pointer" }}
              onClick={() => setOnlyWithoutFw((v) => !v)}
            >
              {t("batchFlash.withoutMatchBadge", { count: pendingWithoutFirmware })}
            </Badge>
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconDownload size={12} />}
              loading={exportingFw}
              onClick={() => void exportWithoutFirmware()}
            >
              {t("batchFlash.exportWithoutFw")}
            </Button>
          </>
        )}
      </Group>

      <Card withBorder mb="md">
        <Stack gap="sm">
          <Group justify="space-between" align="end">
            <Title order={4}>{t("batchFlash.quick.title")}</Title>
            <Text size="xs" c="dimmed">{t("batchFlash.quick.hint")}</Text>
          </Group>

          <form
            onSubmit={(e) => {
              // Enter searches the firmware (preview), it does NOT flash.
              e.preventDefault();
              void searchPreview();
            }}
          >
            <Group align="end" wrap="nowrap">
              <TextInput
                ref={serialInputRef}
                label={t("batchFlash.quick.serialLabel")}
                placeholder={t("batchFlash.quick.serialPlaceholder")}
                defaultValue=""
                onChange={() => {
                  // Uncontrolled: no per-keystroke state. Only clear a stale
                  // preview once, when one is showing (avoids re-render churn).
                  if (previewState !== "idle") {
                    setPreviewDevice(null);
                    setPreviewState("idle");
                  }
                }}
                disabled={flashingRef.current}
                autoFocus
                maxLength={32}
                style={{ flex: 1 }}
                ff="monospace"
              />
              <Button
                type="button"
                variant="default"
                leftSection={<IconSearch size={16} />}
                onClick={() => void searchPreview()}
                disabled={flashingRef.current}
              >
                {t("batchFlash.quick.searchButton")}
              </Button>
              <Button
                type="button"
                leftSection={<IconBolt size={16} />}
                onClick={() => void handleQuickFlash()}
                disabled={
                  !canEdit ||
                  flashingRef.current ||
                  previewState === "not_found" ||
                  previewState === "missing_fw"
                }
                loading={flashingRef.current}
              >
                {t("batchFlash.quick.flashButton")}
              </Button>
            </Group>
          </form>

          {previewState === "idle" && (
            <Text size="sm" c="dimmed">{t("batchFlash.quick.pressEnter")}</Text>
          )}
          <QuickPreview state={previewState} device={previewDevice} />
        </Stack>
      </Card>

      <Group align="stretch" wrap="wrap" grow>
        <Card withBorder>
          <Group justify="space-between" mb="sm">
            <Title order={4}>{t("batchFlash.fwSection")}</Title>
            {canEdit && (
              <Group gap="xs">
                <FileButton onChange={handleUpload} accept=".elf" multiple>
                  {(props) => (
                    <Button
                      {...props}
                      leftSection={<IconUpload size={14} />}
                      loading={uploading}
                      variant="light"
                    >
                      {t("batchFlash.upload")}
                    </Button>
                  )}
                </FileButton>
                <Button
                  variant="default"
                  leftSection={<IconCopy size={14} />}
                  onClick={() => void openImportModal()}
                >
                  {t("batchFlash.importFromBatch")}
                </Button>
                {firmware.length > 0 && (
                  <Button
                    color="red"
                    variant="subtle"
                    leftSection={<IconTrash size={14} />}
                    onClick={() => void handleDeleteAll()}
                  >
                    {t("batchFlash.deleteAll")}
                  </Button>
                )}
              </Group>
            )}
          </Group>

          {uploadProgress && (
            <Stack gap={2} mb="sm">
              <Progress
                value={uploadProgress.total ? (uploadProgress.done / uploadProgress.total) * 100 : 0}
                animated
              />
              <Text size="xs" c="dimmed">
                {t("batchFlash.uploadProgress", {
                  done: uploadProgress.done,
                  total: uploadProgress.total,
                  pct: uploadProgress.total
                    ? Math.round((uploadProgress.done / uploadProgress.total) * 100)
                    : 0,
                })}
              </Text>
            </Stack>
          )}

          {loading ? (
            <Loader />
          ) : firmware.length === 0 ? (
            <Text
              c="dimmed"
              size="sm"
              dangerouslySetInnerHTML={{ __html: t("batchFlash.noFiles") }}
            />
          ) : (
            <FirmwareTable firmware={firmware} canEdit={canEdit} onDelete={onDeleteFw} />
          )}
        </Card>

        <Card withBorder>
          <Title order={4} mb="sm">
            {t("batchFlash.session")}
          </Title>

          {flash ? (
            <Stack>
              <Group>
                <Badge size="lg" variant="filled" color={stageColor(flash.stage)}>
                  {flash.stage}
                </Badge>
                <Text ff="monospace">
                  serial <b>{flash.device.serial_number}</b> ·{" "}
                  {flash.device.dev_eui.toLowerCase()}
                </Text>
              </Group>
              <Text size="sm">
                Firmware: <Code>{flash.firmware}</Code>
              </Text>
              <Progress
                value={flash.percent}
                animated={!flash.finished}
                striped={!flash.finished}
                color={flash.success === false ? "red" : flash.success ? "green" : "blue"}
              />
              <ScrollArea h={140}>
                <Code block fz="xs">
                  {flash.log.length > 0 ? flash.log.join("\n") : "(awaiting output...)"}
                </Code>
              </ScrollArea>
              {flash.error && (
                <Alert color="red" icon={<IconX size={16} />} title={t("batchFlash.errorTitle")}>
                  {flash.error}
                </Alert>
              )}
              {flash.finished && (
                <Alert
                  color={flash.success ? "green" : "red"}
                  icon={flash.success ? <IconCheck size={16} /> : <IconX size={16} />}
                  title={flash.success ? t("batchFlash.success") : t("batchFlash.failed")}
                >
                  Device #{flash.device.id} · serial {flash.device.serial_number}
                </Alert>
              )}
              {flash.finished && (
                <Group>
                  <Button
                    size="lg"
                    leftSection={<IconBolt size={18} />}
                    disabled={!nextDevice || !canEdit}
                    onClick={() => nextDevice && startFlash(nextDevice)}
                  >
                    {t("batchFlash.flashNext", { serial: nextDevice?.serial_number ?? t("common.none") })}
                  </Button>
                  <Button variant="subtle" onClick={() => setFlash(null)}>
                    {t("batchFlash.close")}
                  </Button>
                </Group>
              )}
            </Stack>
          ) : nextDevice ? (
            <Stack>
              <Text size="sm">{t("batchFlash.nextDevice")}</Text>
              <Group>
                <Badge size="lg" variant="light">
                  serial {nextDevice.serial_number}
                </Badge>
                <Text ff="monospace" size="sm">
                  {nextDevice.dev_eui.toLowerCase()}
                </Text>
              </Group>
              <Text size="sm">
                Firmware: <Code>{nextDevice.firmware_filename}</Code>
              </Text>
              <Button
                size="lg"
                leftSection={<IconBolt size={18} />}
                onClick={() => startFlash(nextDevice)}
                disabled={!canEdit}
              >
                {t("batchFlash.flashNextSimple")}
              </Button>
              <Text size="xs" c="dimmed">
                {t("batchFlash.connectHint")}
              </Text>
            </Stack>
          ) : (
            <Text
              c="dimmed"
              size="sm"
              dangerouslySetInnerHTML={{ __html: t("batchFlash.noPending") }}
            />
          )}
        </Card>
      </Group>

      <Card withBorder mt="md">
        <Group justify="space-between" mb="xs">
          <Title order={5}>{t("batchFlash.history.title")}</Title>
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconRefresh size={12} />}
            onClick={async () => {
              try {
                setHistory(await api.listFlashHistory(batchId, 50));
              } catch {
                /* non-fatal */
              }
            }}
          >
            {t("batchFlash.history.refresh")}
          </Button>
        </Group>
        <ResponsiveTable
          stagger={false}
          mobile={{
            rows: history,
            rowKey: (e) => e.id,
            title: (e) => <Text span ff="monospace" size="sm" fw={600}>{e.serial_number ?? "—"}</Text>,
            subtitle: (e) => (e.created_at ? new Date(e.created_at).toLocaleString() : "—"),
            badge: (e) => (
              <Badge size="sm" variant="light" color={e.success ? "green" : "red"}>
                {e.success ? t("batchFlash.history.success") : t("batchFlash.history.failed")}
              </Badge>
            ),
            fields: [
              { label: "DEV_EUI", render: (e) => <Text ff="monospace" size="xs">{e.dev_eui.toLowerCase()}</Text> },
              { label: t("batchFlash.history.colFirmware"), render: (e) => <Text size="xs" ff="monospace">{e.firmware_filename ?? "—"}</Text> },
              { label: t("batchFlash.history.colUser"), render: (e) => <Text size="xs" c="dimmed">{e.username ?? "—"}</Text> },
            ],
            empty: t("batchFlash.history.empty"),
          }}
        >
        <ScrollArea h={220} type="auto">
          <Table striped withTableBorder miw={680}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("batchFlash.history.colTime")}</Table.Th>
                <Table.Th>{t("batchFlash.history.colSerial")}</Table.Th>
                <Table.Th>DEV_EUI</Table.Th>
                <Table.Th style={{ whiteSpace: "nowrap" }}>{t("batchFlash.history.colStatus")}</Table.Th>
                <Table.Th>{t("batchFlash.history.colFirmware")}</Table.Th>
                <Table.Th>{t("batchFlash.history.colUser")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {history.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text c="dimmed" size="sm">{t("batchFlash.history.empty")}</Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                history.map((e) => (
                  <Table.Tr key={e.id}>
                    <Table.Td>
                      <Text size="xs" ff="monospace">
                        {e.created_at ? new Date(e.created_at).toLocaleString() : "—"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text ff="monospace" size="sm">{e.serial_number ?? "—"}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text ff="monospace" size="xs">{e.dev_eui.toLowerCase()}</Text>
                    </Table.Td>
                    <Table.Td style={{ whiteSpace: "nowrap" }}>
                      <Badge size="sm" variant="light" color={e.success ? "green" : "red"}>
                        {e.success ? t("batchFlash.history.success") : t("batchFlash.history.failed")}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" ff="monospace">{e.firmware_filename ?? "—"}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">{e.username ?? "—"}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        </ResponsiveTable>
      </Card>

      <Card withBorder mt="md">
        <Title order={5} mb="xs">
          {t("batchFlash.pendingDevices")}
        </Title>
        <ResponsiveTable
          stagger={false}
          mobile={{
            rows: displayedQueue.slice(0, 200),
            rowKey: (d) => d.id,
            title: (d) => <Text span ff="monospace" size="sm" fw={600}>{d.serial_number ?? t("common.none")}</Text>,
            subtitle: (d) => d.dev_eui.toLowerCase(),
            badge: (d) => <Badge size="sm" variant="light">{d.status}</Badge>,
            fields: [
              {
                label: t("batchFlash.colFwMatch"),
                render: (d) => d.firmware_filename ? (
                  <Code>{d.firmware_filename}</Code>
                ) : (
                  <Text c="yellow" size="sm">{t("batchFlash.fwMissing")}</Text>
                ),
              },
            ],
            actions: (d) => (
              <Button
                size="sm"
                variant="light"
                disabled={!d.firmware_filename || !canEdit || flashingRef.current}
                onClick={() => startFlash(d)}
                leftSection={<IconBolt size={14} />}
              >
                {t("batchFlash.flashShort")}
              </Button>
            ),
            empty: onlyWithoutFw ? t("batchFlash.noWithoutFw") : t("batchFlash.allFlashed"),
          }}
        >
        <ScrollArea h={360} type="auto">
          <Table striped withTableBorder miw={680}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("batchFlash.colSerial")}</Table.Th>
                <Table.Th>DEV_EUI</Table.Th>
                <Table.Th>{t("batchFlash.colFwMatch")}</Table.Th>
                <Table.Th>{t("batchFlash.colStatus")}</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {displayedQueue.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text c="dimmed" size="sm">
                      {onlyWithoutFw ? t("batchFlash.noWithoutFw") : t("batchFlash.allFlashed")}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                displayedQueue.slice(0, 200).map((d) => (
                  <Table.Tr key={d.id}>
                    <Table.Td>
                      <Text ff="monospace" size="sm">
                        {d.serial_number ?? t("common.none")}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text ff="monospace" size="sm">
                        {d.dev_eui.toLowerCase()}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {d.firmware_filename ? (
                        <Code>{d.firmware_filename}</Code>
                      ) : (
                        <Text c="yellow" size="sm">
                          {t("batchFlash.fwMissing")}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="light">
                        {d.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Tooltip
                        label={
                          d.firmware_filename
                            ? t("batchFlash.flashNow")
                            : t("batchFlash.fwMissingTooltip")
                        }
                      >
                        <Button
                          size="xs"
                          variant="light"
                          disabled={!d.firmware_filename || !canEdit || flashingRef.current}
                          onClick={() => startFlash(d)}
                          leftSection={<IconBolt size={12} />}
                        >
                          {t("batchFlash.flashShort")}
                        </Button>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        </ResponsiveTable>
        {displayedQueue.length > 200 && (
          <Text size="xs" c="dimmed" mt="xs">
            {t("batchFlash.first200", { total: displayedQueue.length })}
          </Text>
        )}
      </Card>

      <Modal
        opened={importOpen}
        onClose={() => setImportOpen(false)}
        title={t("batchFlash.importFromBatch")}
        size="lg"
        centered
      >
        <Stack>
          {importLoading ? (
            <Loader />
          ) : importBatches.length === 0 ? (
            <Text c="dimmed" size="sm">{t("batchFlash.importNoBatches")}</Text>
          ) : (() => {
            // Flatten all files across batches into a single list
            const allItems: { batch_id: number; batch_code: string; file: FirmwareFile }[] = [];
            for (const b of importBatches) {
              for (const f of b.files) {
                allItems.push({ batch_id: b.batch_id, batch_code: b.batch_code, file: f });
              }
            }
            const q = importSearch.trim().toLowerCase();
            const filtered = allItems.filter((it) => {
              if (importOnlyMatching && !fileMatches(it.file)) return false;
              if (!q) return true;
              return (
                it.file.filename.toLowerCase().includes(q) ||
                it.file.supplier_id.includes(q) ||
                it.batch_code.toLowerCase().includes(q)
              );
            });
            const matchingTotal = allItems.filter((it) => fileMatches(it.file)).length;
            const allChecked = filtered.length > 0 &&
              filtered.every((it) => importSelected.has(selKey(it.batch_id, it.file.filename)));
            return (
              <>
                <Group gap="xs" wrap="nowrap">
                  <TextInput
                    placeholder={t("batchFlash.importSearchPlaceholder")}
                    value={importSearch}
                    onChange={(e) => setImportSearch(e.currentTarget.value)}
                    style={{ flex: 1 }}
                  />
                  <Checkbox
                    label={t("batchFlash.importOnlyMatching", { count: matchingTotal })}
                    checked={importOnlyMatching}
                    onChange={(e) => setImportOnlyMatching(e.currentTarget.checked)}
                  />
                </Group>
                <Group justify="space-between">
                  <Checkbox
                    label={t("batchFlash.importSelectAll")}
                    checked={allChecked}
                    indeterminate={
                      !allChecked &&
                      filtered.some((it) => importSelected.has(selKey(it.batch_id, it.file.filename)))
                    }
                    onChange={() => toggleImportAll(filtered.map((it) => ({ batch_id: it.batch_id, file: it.file })))}
                  />
                  <Text size="xs" c="dimmed">
                    {t("batchFlash.importSelectedCount", { count: importSelected.size, total: filtered.length })}
                  </Text>
                </Group>
                <ScrollArea h={360}>
                  <Stack gap={4}>
                    {filtered.length === 0 ? (
                      <Text size="sm" c="dimmed">{t("batchFlash.importNoMatches")}</Text>
                    ) : (
                      filtered.map((it) => {
                        const matches = fileMatches(it.file);
                        const k = selKey(it.batch_id, it.file.filename);
                        return (
                          <Checkbox
                            key={k}
                            checked={importSelected.has(k)}
                            onChange={() => toggleImportFile(it.batch_id, it.file.filename)}
                            label={
                              <Group gap="xs" wrap="wrap">
                                <Text ff="monospace" size="sm">{it.file.filename}</Text>
                                <Text size="xs" c="dimmed">({(it.file.size / 1024).toFixed(1)} KB)</Text>
                                <Badge size="xs" variant="outline">{it.batch_code}</Badge>
                                {matches && (
                                  <Badge size="xs" color="teal" variant="light">
                                    {t("batchFlash.importMatch")}
                                  </Badge>
                                )}
                              </Group>
                            }
                          />
                        );
                      })
                    )}
                  </Stack>
                </ScrollArea>
                <Group justify="flex-end">
                  <Button variant="subtle" onClick={() => setImportOpen(false)} disabled={importing}>
                    {t("common.cancel")}
                  </Button>
                  <Button
                    onClick={() => void runImport()}
                    loading={importing}
                    disabled={importSelected.size === 0}
                  >
                    {t("batchFlash.importButton", { count: importSelected.size })}
                  </Button>
                </Group>
              </>
            );
          })()}
        </Stack>
      </Modal>
    </Container>
  );
}

function stageColor(stage: string): string {
  switch (stage) {
    case "connecting":
    case "starting":
      return "gray";
    case "erasing":
      return "orange";
    case "downloading":
      return "blue";
    case "verifying":
      return "violet";
    case "done":
      return "green";
    case "failed":
      return "red";
    default:
      return "gray";
  }
}

function QuickPreview({
  state,
  device,
}: {
  state: "idle" | "loading" | "ok" | "missing_fw" | "not_found" | "error";
  device: DeviceLookup | null;
}) {
  const { t } = useTranslation();
  if (state === "idle") return null;
  if (state === "loading") {
    return <Text size="sm" c="dimmed">{t("batchFlash.quick.previewLoading")}</Text>;
  }
  if (state === "not_found") {
    return <Alert color="red">{t("batchFlash.quick.notFoundShort")}</Alert>;
  }
  if (state === "error") {
    return <Alert color="gray">{t("batchFlash.quick.previewError")}</Alert>;
  }
  if (!device) return null;
  return (
    <Alert color={state === "ok" ? "green" : "yellow"}>
      <Group gap="md" wrap="wrap">
        <Text size="sm">
          <b>#{device.id}</b> · <Code>{device.dev_eui.toLowerCase()}</Code>
        </Text>
        {device.firmware_filename ? (
          <Text size="sm">FW: <Code>{device.firmware_filename}</Code></Text>
        ) : (
          <Text size="sm" c="dimmed">{t("batchFlash.quick.missingFwShort")}</Text>
        )}
        {device.firmware_borrowed_from != null && (
          <Text size="xs" c="teal">
            {t("batchFlash.quick.borrowedFrom", { batchId: device.firmware_borrowed_from })}
          </Text>
        )}
      </Group>
    </Alert>
  );
}

type FwSortKey = "supplier_id" | "filename" | "size";
type SortDir = "asc" | "desc";
const FW_PAGE_SIZE = 100;

// Batches can hold ~10k firmware files: render one page at a time, and memo
// so unrelated page state (flash progress, preview) doesn't re-render it.
const FirmwareTable = memo(function FirmwareTable({
  firmware,
  canEdit,
  onDelete,
}: {
  firmware: FirmwareFile[];
  canEdit: boolean;
  onDelete: (filename: string) => void;
}) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState<FwSortKey>("supplier_id");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);

  function toggleSort(key: FwSortKey) {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = q ? firmware.filter((f) => f.filename.toLowerCase().includes(q)) : [...firmware];
    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      switch (sortKey) {
        case "supplier_id": return (Number(a.supplier_id) - Number(b.supplier_id)) * dir;
        case "size": return (a.size - b.size) * dir;
        case "filename": return a.filename.localeCompare(b.filename) * dir;
      }
    });
    return list;
  }, [firmware, filter, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / FW_PAGE_SIZE));
  const curPage = Math.min(page, totalPages);
  const rows = filtered.slice((curPage - 1) * FW_PAGE_SIZE, curPage * FW_PAGE_SIZE);

  function SortHeader({ k, label }: { k: FwSortKey; label: string }) {
    const isActive = sortKey === k;
    const Icon = isActive ? (sortDir === "asc" ? IconArrowUp : IconArrowDown) : IconArrowsSort;
    return (
      <UnstyledButton
        onClick={() => toggleSort(k)}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", fontWeight: 600 }}
      >
        {label}
        <Icon size={12} style={{ opacity: isActive ? 1 : 0.4 }} />
      </UnstyledButton>
    );
  }

  return (
    <Stack gap="xs">
      <TextInput
        size="xs"
        leftSection={<IconSearch size={14} />}
        placeholder={t("batchFlash.fwFilter")}
        value={filter}
        onChange={(e) => {
          setFilter(e.currentTarget.value);
          setPage(1);
        }}
      />
      <ResponsiveTable
        stagger={false}
        mobile={{
          rows,
          sort: {
            options: [
              { value: "supplier_id", label: t("batchFlash.colSerial") },
              { value: "filename", label: t("batchFlash.colFilename") },
              { value: "size", label: t("batchFlash.colSize") },
            ],
            value: sortKey,
            dir: sortDir,
            onChange: (v, d) => (v === sortKey ? setSortDir(d) : toggleSort(v as FwSortKey)),
          },
          rowKey: (f) => f.filename,
          title: (f) => <Text span size="sm" fw={600}>{f.filename}</Text>,
          subtitle: (f) => `${(f.size / 1024).toFixed(1)} KB`,
          fields: [
            { label: t("batchFlash.colSerial"), render: (f) => <Text ff="monospace" size="sm">{f.supplier_id}</Text> },
          ],
          actions: canEdit ? (f) => (
            <ActionIcon color="red" variant="subtle" size="lg" onClick={() => onDelete(f.filename)}>
              <IconTrash size={18} />
            </ActionIcon>
          ) : undefined,
        }}
      >
      <ScrollArea h={300} type="auto">
        <Table striped withTableBorder miw={420}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th><SortHeader k="supplier_id" label={t("batchFlash.colSerial")} /></Table.Th>
              <Table.Th><SortHeader k="filename" label={t("batchFlash.colFilename")} /></Table.Th>
              <Table.Th><SortHeader k="size" label={t("batchFlash.colSize")} /></Table.Th>
              {canEdit && <Table.Th />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((f) => (
              <Table.Tr key={f.filename}>
                <Table.Td>
                  <Text ff="monospace" size="sm">
                    {f.supplier_id}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{f.filename}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {(f.size / 1024).toFixed(1)} KB
                  </Text>
                </Table.Td>
                {canEdit && (
                  <Table.Td>
                    <ActionIcon color="red" variant="subtle" onClick={() => onDelete(f.filename)}>
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
      </ResponsiveTable>
      <Group justify="space-between">
        <Text size="xs" c="dimmed">
          {t("batchFlash.fwShowing", { shown: rows.length, total: filtered.length })}
        </Text>
        {totalPages > 1 && <Pagination size="sm" total={totalPages} value={curPage} onChange={setPage} />}
      </Group>
    </Stack>
  );
});
